import { getWindow } from '../general';
import { getSvocId, getUserId } from '../user';
import { ISessionPHFilter } from '../../models/purchase-history';

const PH_FILTER_SESSION_KEY = 'PHFilter';

// Discards a PHFilter payload saved by a different account or, within a
// shared-org svocID, a different user, mirroring the svocID guard
// PHODPageData() already applies to the sibling 'purchaseHistoryPage' key.
// An absent/unverifiable svocID or userID (on either side) is treated as a
// mismatch rather than a pass, so a legacy or partially-stamped entry can't
// be trusted just because there's nothing to compare it against.
export const sanitizePHFilterForCurrentUser = (
  filter: ISessionPHFilter,
): ISessionPHFilter => {
  const svocIdMismatch = !filter.svocID || filter.svocID !== getSvocId();
  const userIdMismatch = !filter.userID || filter.userID !== getUserId();
  if (svocIdMismatch || userIdMismatch) {
    getWindow()?.sessionStorage?.removeItem(PH_FILTER_SESSION_KEY);
    return {};
  }
  return filter;
};

export const getPHFilterFromSession = (): ISessionPHFilter => {
  try {
    const filterString = getWindow()?.sessionStorage?.getItem(
      PH_FILTER_SESSION_KEY,
    );
    const filter = JSON.parse(filterString || '{}');
    return sanitizePHFilterForCurrentUser(filter);
  } catch {
    return {};
  }
};

export const setPHFilterToSession = (
  filterSettings: Partial<ISessionPHFilter>,
): void => {
  const PHFilterSession = getPHFilterFromSession();
  getWindow()?.sessionStorage?.setItem(
    PH_FILTER_SESSION_KEY,
    JSON.stringify({
      ...PHFilterSession,
      ...filterSettings,
      svocID: getSvocId(),
      userID: getUserId(),
    }),
  );
};

/**
 * Parses a date string in YYYY-MM-DD format to MM/DD/YYYY format.
 * @param date - Date string in YYYY-MM-DD format or null
 * @returns Formatted date string in MM/DD/YYYY format or '00/00/0'
 */
export const parseDateToString = (date: string | null): string => {
  if (!date) return '00/00/0';
  if (date.includes('/')) return date;
  const [year, month, day] = date.split('-').map(Number);
  const monthString = month < 10 ? `0${month}` : `${month}`;
  const dayString = day < 10 ? `0${day}` : `${day}`;
  return `${monthString}/${dayString}/${year}`;
};

/**
 * Converts date strings or DateTime objects from MM/DD/YYYY to YYYY-MM-DD format.
 * @param startDate - Start date in MM/DD/YYYY or DateTime format
 * @param endDate - End date in MM/DD/YYYY or DateTime format
 * @returns Array [newStartDate, newEndDate] in YYYY-MM-DD format or empty strings
 */
export const modifyDateFormat = (
  startDate: string | { toString: () => string } | null,
  endDate: string | { toString: () => string } | null,
): [string, string] => {
  const modifiedStartDate = startDate?.toString().split('/');
  const modifiedEndDate = endDate?.toString().split('/');

  const newStartDate =
    modifiedStartDate && modifiedStartDate.length === 3
      ? `${modifiedStartDate[2]}-${modifiedStartDate[0]}-${modifiedStartDate[1]}`
      : '';
  const newEndDate =
    modifiedEndDate && modifiedEndDate.length === 3
      ? `${modifiedEndDate[2]}-${modifiedEndDate[0]}-${modifiedEndDate[1]}`
      : '';

  return [newStartDate, newEndDate];
};
